'use client'

import * as React from 'react'
import { Command } from 'cmdk'
import { Check, ChevronDown, Search } from 'lucide-react'

import { cn } from '@/lib/utils'
import { filterComboboxOptions } from '@/lib/combobox-filter'

/**
 * Searchable single-select picker for long lists (members, households) —
 * the scroll-only Select stops working past a few dozen entries.
 *
 * Input-first (Tova, 2026-08-11): the field IS the search box. Focus it and
 * type — results populate underneath; no button-then-search two-step.
 * Closed, the field reads as the current choice; open, it's a fresh query
 * with the choice as its placeholder.
 *
 * The result list is an inline absolutely-positioned panel, NOT a portalled
 * Radix Popover. That is deliberate: inside a modal Dialog/Sheet, a
 * portalled panel is "outside" the dialog's DOM, so clicking a result
 * dismisses the whole dialog (non-modal popover), while a modal popover
 * steals focus from the input. Inline, clicks are inside the dialog and
 * focus never leaves the input. Escape is intercepted on WINDOW in the
 * capture phase — window-capture runs before Radix's document-capture
 * dismiss listener, and preventDefault() makes Radix skip closing the
 * dialog — so Escape closes the list first, the dialog second.
 *
 * cmdk supplies combobox ARIA and arrow/Enter keyboard behavior. Filtering
 * is manual substring matching (filterComboboxOptions) so `pinned` options
 * ("No linked member") stay visible no matter what's typed. Options can
 * carry `hint` (secondary line, e.g. household name) and `keywords` (extra
 * search terms beyond the label).
 */

export type ComboboxOption = {
    value: string
    label: string
    /** Secondary line under the label, e.g. the member's household. */
    hint?: string
    /** Extra strings the search matches beyond the label. */
    keywords?: string[]
}

interface ComboboxProps {
    options: ComboboxOption[]
    /** Always-visible entries above the searchable list, e.g. a "none" choice. */
    pinned?: ComboboxOption[]
    value: string
    onValueChange: (value: string) => void
    id?: string
    placeholder?: string
    searchPlaceholder?: string
    emptyMessage?: string
    disabled?: boolean
    /** Danger state (Input idiom): sets aria-invalid and the danger border. */
    error?: boolean
    /** Id of the error/help text element, announced with the field. */
    'aria-describedby'?: string
}

export function Combobox({
    options,
    pinned = [],
    value,
    onValueChange,
    id,
    placeholder = 'Choose…',
    searchPlaceholder = 'Type to search…',
    emptyMessage = 'No matches.',
    disabled,
    error,
    'aria-describedby': ariaDescribedby,
}: ComboboxProps) {
    const [open, setOpen] = React.useState(false)
    const [query, setQuery] = React.useState('')
    const rootRef = React.useRef<HTMLDivElement>(null)
    const inputRef = React.useRef<HTMLInputElement>(null)

    const selected =
        pinned.find((o) => o.value === value) ?? options.find((o) => o.value === value)
    const filtered = filterComboboxOptions(options, query)

    const close = React.useCallback(() => {
        setOpen(false)
        setQuery('')
    }, [])

    // cmdk overrides the input's id with its own generated one on every
    // render ({...props, id: inputId} internally), which would orphan the
    // caller's <Label htmlFor>. Re-assert ours after each commit.
    React.useLayoutEffect(() => {
        if (id && inputRef.current) inputRef.current.setAttribute('id', id)
    })

    // Close on click/tap anywhere outside the field + list. Deliberately not
    // blur-based: a mousedown on a result blurs the input before the click
    // lands, which would close the list under the cursor.
    React.useEffect(() => {
        if (!open) return
        function onPointerDown(event: PointerEvent) {
            if (!rootRef.current?.contains(event.target as Node)) close()
        }
        document.addEventListener('pointerdown', onPointerDown)
        return () => document.removeEventListener('pointerdown', onPointerDown)
    }, [open, close])

    // Escape closes the list WITHOUT closing a surrounding Radix Dialog or
    // Sheet. Radix dismisses on a document-level CAPTURE listener, so a
    // handler on the input fires too late; window-capture fires before
    // document-capture, and preventDefault() makes Radix skip its dismiss.
    React.useEffect(() => {
        if (!open) return
        function onKeyDown(event: KeyboardEvent) {
            if (event.key !== 'Escape') return
            event.preventDefault()
            // No refocus here: focus is already in the input when typing, and
            // a programmatic focus() would re-fire onFocus → reopen the list.
            close()
        }
        window.addEventListener('keydown', onKeyDown, { capture: true })
        return () => window.removeEventListener('keydown', onKeyDown, { capture: true })
    }, [open, close])

    function select(next: string) {
        onValueChange(next)
        close()
    }

    return (
        <Command shouldFilter={false} className="relative" ref={rootRef}>
            <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--icon-secondary)]" />
                <Command.Input
                    ref={inputRef}
                    disabled={disabled}
                    aria-invalid={error || undefined}
                    aria-describedby={ariaDescribedby}
                    value={open ? query : (selected?.label ?? '')}
                    onValueChange={(next) => {
                        setQuery(next)
                        if (!open) setOpen(true)
                    }}
                    onFocus={() => setOpen(true)}
                    onClick={() => setOpen(true)}
                    placeholder={selected ? selected.label : open ? searchPlaceholder : placeholder}
                    onKeyDown={(event) => {
                        if (event.key === 'Tab') close()
                    }}
                    className={cn(
                        'h-10 w-full rounded-md border border-[var(--border-input)] bg-[var(--bg-surface)] py-2 pl-9 pr-9 text-sm text-[var(--text-primary)] transition-colors duration-200',
                        'placeholder:text-[var(--text-tertiary)]',
                        'focus:outline-none focus:border-[var(--border-focus)] focus:ring-2 focus:ring-[var(--primary-200)]',
                        'disabled:cursor-not-allowed disabled:bg-[var(--bg-subtle)] disabled:border-[var(--border-subtle)] disabled:text-[var(--text-disabled)]',
                        open && !error && 'border-[var(--border-brand)]',
                        error &&
                            'border-[var(--border-danger)] focus:border-[var(--border-danger)] focus:ring-[var(--danger-100)]'
                    )}
                />
                <button
                    type="button"
                    tabIndex={-1}
                    aria-hidden="true"
                    disabled={disabled}
                    onClick={() => {
                        if (open) {
                            close()
                        } else {
                            inputRef.current?.focus()
                        }
                    }}
                    className="absolute right-0 top-0 flex h-10 w-9 items-center justify-center text-[var(--icon-secondary)] disabled:text-[var(--text-disabled)]"
                >
                    <ChevronDown
                        className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-180')}
                    />
                </button>
            </div>
            {open && (
                <div className="absolute top-full z-50 mt-1 w-full rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-lg">
                    <Command.List className="max-h-56 overflow-y-auto p-1">
                        {pinned.map((option) => (
                            <ComboboxItem
                                key={option.value}
                                option={option}
                                selected={option.value === value}
                                onSelect={select}
                            />
                        ))}
                        {pinned.length > 0 && filtered.length > 0 && (
                            <div className="mx-1 my-1 h-px bg-[var(--border-subtle)]" />
                        )}
                        {filtered.map((option) => (
                            <ComboboxItem
                                key={option.value}
                                option={option}
                                selected={option.value === value}
                                onSelect={select}
                            />
                        ))}
                        {filtered.length === 0 && (
                            <p className="px-3 py-6 text-center text-sm text-[var(--text-secondary)]">
                                {emptyMessage}
                            </p>
                        )}
                    </Command.List>
                </div>
            )}
        </Command>
    )
}

function ComboboxItem({
    option,
    selected,
    onSelect,
}: {
    option: ComboboxOption
    selected: boolean
    onSelect: (value: string) => void
}) {
    return (
        <Command.Item
            // cmdk needs a unique value even with filtering off; it may
            // normalize the string it passes back, so select via closure.
            value={option.value}
            onSelect={() => onSelect(option.value)}
            className={cn(
                'flex cursor-pointer select-none items-center justify-between gap-2 rounded-md px-3 py-2 text-sm',
                'data-[selected=true]:bg-[var(--bg-subtle)]'
            )}
        >
            <span className="min-w-0">
                <span className="block truncate">{option.label}</span>
                {option.hint && (
                    <span className="block truncate text-xs text-[var(--text-tertiary)]">
                        {option.hint}
                    </span>
                )}
            </span>
            {selected && <Check className="h-4 w-4 shrink-0 text-[var(--icon-brand)]" />}
        </Command.Item>
    )
}
